import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import { getSiteAdminTemplatesRoute, getSiteAdminTemplateCategoriesRoute, updateTemplateFlagsRoute } from '../../../api/routes';
import { ReactComponent as PencilIcon } from '../../../assets/icons/Pencil.svg';
import LottieIcon from '../../../components/common/LottieIcon';
import toDisplayAssetUrl from '../../../helpers/DisplayAssetUrl';
import { getTemplateCategoryIds, getTemplateCategoryLabels, templateHasCategory } from '../../../helpers/templateCategories';
import '../SiteAdmin.scss';

// Group templates: campaign templates go under 'Campaign', feature templates group by featureType.
// Within each group, sort by category name then by template name.
const groupByFeatureType = (templates, categoryMap = {}) => {
    const groups = {};
    templates.forEach(t => {
        const key = t.templateType === 'campaign' ? 'Campaign' : (t.featureType || 'Uncategorised');
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
    });
    return Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([groupName, groupTemplates]) => [
            groupName,
            [...groupTemplates].sort((a, b) => {
                const catA = getTemplateCategoryLabels(a, categoryMap).join(', ').toLowerCase();
                const catB = getTemplateCategoryLabels(b, categoryMap).join(', ').toLowerCase();
                if (catA < catB) return -1;
                if (catA > catB) return 1;
                return (a.name || '').localeCompare(b.name || '');
            }),
        ]);
};

// ── Toggle component ───────────────────────────────────────────────────────────

const FlagToggle = ({ label, value, onChange, saving }) => (
    <div className="tm-flag" onClick={() => !saving && onChange(!value)}>
        <div className={`tm-flag-toggle${value ? ' on' : ''}`} />
        <span>{label}</span>
    </div>
);

const DefaultTickButton = ({ value, onChange, saving, disabled }) => (
    <button
        type="button"
        className={`tm-default-button${value ? ' is-default' : ''}${disabled ? ' is-disabled' : ''}`}
        onClick={() => !saving && !disabled && onChange(!value)}
        disabled={saving || disabled}
        title={value ? 'Default template' : 'Set as default template'}
        aria-label={value ? 'Default template' : 'Set as default template'}
    >
        <span className="tm-default-button-check">{value ? '✓' : ''}</span>
    </button>
);

// ── Template row ───────────────────────────────────────────────────────────────

const TemplateRow = ({ template, categoryMap, onFlagChange, onEdit }) => {
    const [saving, setSaving] = useState(false);
    const [descOverflows, setDescOverflows] = useState(false);
    const descTextRef = useRef(null);
    const categoryLabels = getTemplateCategoryLabels(template, categoryMap);

    const handleFlag = async (flag, value) => {
        setSaving(true);
        try {
            const res = await axios.put(
                `${updateTemplateFlagsRoute}/${template.id}/flags`,
                { [flag]: value },
                { withCredentials: true }
            );
            if (res.data.success) {
                onFlagChange(
                    template.id,
                    res.data.template || { ...template, [flag]: value },
                    res.data.clearedTemplateIds || []
                );
            }
        } catch {
            toast.error('Failed to update template');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="template-manager-row">
            <div className="tm-name-column">
                <div
                    className="tm-thumb"
                    style={template.templateImage ? { backgroundImage: `url(${toDisplayAssetUrl(template.templateImage)})` } : {}}
                />
                <div className="tm-info">
                    <div className="tm-name-row">
                        <div className="tm-name">{template.name}</div>
                    </div>
                    <div className="tm-type">
                        {template.templateType === 'campaign' ? 'Campaign template' : template.featureType}
                    </div>
                    {template.description && (
                        <div className="tm-name-description" onMouseEnter={() => {
                            if (descTextRef.current) setDescOverflows(descTextRef.current.scrollHeight > descTextRef.current.clientHeight);
                        }}>
                            <span className="tm-desc-text" ref={descTextRef}>{template.description}</span>
                            {descOverflows && (
                                <div className="tm-desc-tooltip">{template.description}</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="tm-category">
                {categoryLabels.length > 0
                    ? <span className="tm-category-label">{categoryLabels.join(', ')}</span>
                    : <span className="tm-category-none">—</span>}
            </div>
            <div className="tm-feature-icons">
                {(template.features || []).map((f, i) => (
                    f.isStepFeature ? (
                        <div key={i} className={`tm-fi-main category-icon-bg--${((Array.isArray(f.featureCategory) ? f.featureCategory[0] : f.featureCategory) || 'misc').toLowerCase()}`} title={f.contentLabel || f.featureName || f.varKey}>
                            <LottieIcon varKey={f.varKey} size={26} />
                        </div>
                    ) : (
                        <div key={i} className={`tm-fi-disruptor category-icon-bg--${((Array.isArray(f.featureCategory) ? f.featureCategory[0] : f.featureCategory) || 'misc').toLowerCase()}`} title={f.contentLabel || f.featureName || f.varKey}>
                            <LottieIcon varKey={f.varKey} size={13} />
                        </div>
                    )
                ))}
            </div>
            <div className="tm-default-column">
                {template.templateType === 'feature' ? (
                    <DefaultTickButton
                        value={!!template.isDefaultForFeatureType}
                        saving={saving}
                        onChange={v => handleFlag('isDefaultForFeatureType', v)}
                    />
                ) : (
                    <span className="tm-default-placeholder">—</span>
                )}
            </div>
            <div className="tm-visibility-column">
                <FlagToggle
                    label="Active"
                    value={!!template.isActive}
                    saving={saving}
                    onChange={v => handleFlag('isActive', v)}
                />
                <FlagToggle
                    label="Quickstart"
                    value={!!template.isQuickstart}
                    saving={saving}
                    onChange={v => handleFlag('isQuickstart', v)}
                />
                <FlagToggle
                    label="Featured"
                    value={!!template.isFeatured}
                    saving={saving}
                    onChange={v => handleFlag('isFeatured', v)}
                />
            </div>
            <div className="tm-action-column">
                <button type="button" className="tm-edit-icon" onClick={() => onEdit(template)} title="Edit template">
                    <PencilIcon className="tm-pencil" aria-label="Edit" />
                    <span className="tm-edit-label">Edit Template</span>
                </button>
            </div>
        </div>
    );
};

// ── Main TemplateManager ───────────────────────────────────────────────────────

const TemplateManager = ({ onEdit, onCreate, templatePatch, onTemplatesLoaded, onFlagChange: onFlagChangeExternal }) => {
    const [templates, setTemplates] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTemplateTab, setActiveTemplateTab] = useState('feature');
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [filterQuickstart, setFilterQuickstart] = useState('all');
    const [filterFeatured, setFilterFeatured] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');

    useEffect(() => {
        if (templatePatch) {
            setTemplates(prev => prev.map(t => t.id === templatePatch.id ? { ...t, ...templatePatch } : t));
        }
    }, [templatePatch]);

    useEffect(() => {
        Promise.all([
            axios.get(getSiteAdminTemplatesRoute, { withCredentials: true }),
            axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true }),
        ])
            .then(([tRes, cRes]) => {
                if (tRes.data.success) {
                    setTemplates(tRes.data.templates);
                    if (onTemplatesLoaded) onTemplatesLoaded(tRes.data.templates);
                }
                if (cRes.data.success) {
                    const map = {};
                    (cRes.data.categories || []).forEach(c => { map[c.id] = c.name; });
                    setCategoryMap(map);
                }
            })
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFlagChange = (id, patch, clearedTemplateIds = []) => {
        const clearedIds = new Set(clearedTemplateIds);
        setTemplates(prev => prev.map((template) => {
            if (template.id === id) {
                return { ...template, ...patch };
            }
            if (clearedIds.has(template.id)) {
                return { ...template, isDefaultForFeatureType: false };
            }
            return template;
        }));
        if (onFlagChangeExternal) onFlagChangeExternal(id, patch);
    };

    const templatesForTab = useMemo(
        () => templates.filter((template) => (template.templateType || 'feature') === activeTemplateTab),
        [templates, activeTemplateTab]
    );

    let filtered = search
        ? templatesForTab.filter(t =>
            t.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.featureType?.toLowerCase().includes(search.toLowerCase())
          )
        : templatesForTab;
    if (filterActive === 'active') filtered = filtered.filter(t => t.isActive === true);
    if (filterActive === 'inactive') filtered = filtered.filter(t => t.isActive !== true);
    if (filterQuickstart === 'yes') filtered = filtered.filter(t => t.isQuickstart);
    if (filterQuickstart === 'no') filtered = filtered.filter(t => !t.isQuickstart);
    if (filterFeatured === 'yes') filtered = filtered.filter(t => t.isFeatured);
    if (filterFeatured === 'no') filtered = filtered.filter(t => !t.isFeatured);
    if (filterCategory === 'none') filtered = filtered.filter(t => getTemplateCategoryIds(t).length === 0);
    else if (filterCategory !== 'all') filtered = filtered.filter(t => templateHasCategory(t, filterCategory));

    const groups = groupByFeatureType(filtered, categoryMap);
    const featureTemplateCount = templates.filter((template) => (template.templateType || 'feature') === 'feature').length;
    const campaignTemplateCount = templates.filter((template) => template.templateType === 'campaign').length;

    // Sorted category list for the dropdown
    const categoryOptions = Object.entries(categoryMap).sort(([, a], [, b]) => a.localeCompare(b));

    if (loading) return <div className="home-loading-placeholder" />;

    return (
        <div className="template-manager-shell">
            {onCreate && (
                <div className="template-manager-create-row">
                    <div className="standard-button button-active template-manager-create-button template-manager-create-button--feature" onClick={() => onCreate('feature')}>
                        <div className="template-manager-create-icon" aria-hidden="true">
                            <span className="template-manager-create-icon-core">+</span>
                        </div>
                        <span className="template-manager-create-label">Create Feature Template</span>
                    </div>
                    <div className="standard-button template-manager-create-button template-manager-create-button--campaign" onClick={() => onCreate('campaign')}>
                        <div className="template-manager-create-icon template-manager-create-icon--campaign" aria-hidden="true">
                            <span className="template-manager-create-icon-layer template-manager-create-icon-layer--back" />
                            <span className="template-manager-create-icon-layer template-manager-create-icon-layer--front" />
                        </div>
                        <span className="template-manager-create-label">Create Campaign Template</span>
                    </div>
                </div>
            )}
            <div className="template-manager-tabs">
                <div
                    className={`template-manager-tab${activeTemplateTab === 'feature' ? ' active' : ''}`}
                    onClick={() => setActiveTemplateTab('feature')}
                >
                    Feature Templates
                    <span className="template-manager-tab-count">{featureTemplateCount}</span>
                </div>
                <div
                    className={`template-manager-tab${activeTemplateTab === 'campaign' ? ' active' : ''}`}
                    onClick={() => setActiveTemplateTab('campaign')}
                >
                    Campaign Templates
                    <span className="template-manager-tab-count">{campaignTemplateCount}</span>
                </div>
            </div>
            <div className="template-manager-toolbar">
                <input
                    className="sa-text-input"
                    placeholder="Search templates…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <div className="template-manager-filter-row">
                    <select
                        className="sa-select sa-select--narrow"
                        value={filterActive}
                        onChange={e => setFilterActive(e.target.value)}
                    >
                        <option value="all">All Statuses</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>
                    <select
                        className="sa-select sa-select--narrow"
                        value={filterQuickstart}
                        onChange={e => setFilterQuickstart(e.target.value)}
                    >
                        <option value="all">All Quickstart</option>
                        <option value="yes">Quickstart Only</option>
                        <option value="no">Not Quickstart</option>
                    </select>
                    <select
                        className="sa-select sa-select--narrow"
                        value={filterFeatured}
                        onChange={e => setFilterFeatured(e.target.value)}
                    >
                        <option value="all">All Featured</option>
                        <option value="yes">Featured Only</option>
                        <option value="no">Not Featured</option>
                    </select>
                    <select
                        className="sa-select sa-select--narrow"
                        value={filterCategory}
                        onChange={e => setFilterCategory(e.target.value)}
                    >
                        <option value="all">All Categories</option>
                        {categoryOptions.map(([id, name]) => (
                            <option key={id} value={id}>{name}</option>
                        ))}
                    </select>
                </div>
                <span className="template-manager-summary">
                    {filtered.length} template{filtered.length !== 1 ? 's' : ''}
                    {' · '}
                    {filtered.filter(t => t.isQuickstart).length} quickstart
                    {' · '}
                    {filtered.filter(t => t.isFeatured).length} featured
                    {filtered.length !== templatesForTab.length && (
                        <>{' · '}out of {templatesForTab.length} in this tab</>
                    )}
                </span>
            </div>

            <div className="template-manager-grid">
                {groups.map(([groupName, groupTemplates]) => (
                    <div key={groupName} className="template-manager-group">
                        <div className="template-manager-group-title">{groupName}</div>
                        <div className="template-manager-group-body">
                            <div className="template-manager-header-row">
                                <div className="tm-header-cell tm-header-cell--name">Feature Name</div>
                                <div className="tm-header-cell tm-header-cell--category">Category</div>
                                <div className="tm-header-cell tm-header-cell--components">Components Used</div>
                                <div className="tm-header-cell tm-header-cell--default">Default Template</div>
                                <div className="tm-header-cell tm-header-cell--visibility">Visibility</div>
                                <div className="tm-header-cell tm-header-cell--actions">Edit</div>
                            </div>
                            {groupTemplates.map(t => (
                                <TemplateRow key={t.id} template={t} categoryMap={categoryMap} onFlagChange={handleFlagChange} onEdit={onEdit} />
                            ))}
                        </div>
                    </div>
                ))}

                {groups.length === 0 && (
                    <div className="template-manager-empty-state">
                        No templates found.
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateManager;
